<script setup>
/*
    Toolbar widget

    Components:
        primary: <v-toolbar>

    Widget Properties:
        value: []

    Component Args

    CSS Properties:
 */
import {onMounted, onUnmounted, reactive, ref, watch} from 'vue'
import {
    Feedback,
    Progress,
    State,
    can,
    clone,
    getModels,
    getRoute,
    getRoutes,
    getValue,
    go,
    waitRender,
} from '@/paks/vu-app'
import {getView, getWidgetProps, persistDisplay, updateView} from '@/paks/vu-dash/BoardUtils.js'

defineExpose({update})

const props = defineProps({
    board: Object,
    class: String,
    design: Boolean,
    state: Object,
    view: Object,
    widget: {type: Object, default: {}},
})

const page = reactive({
    args: {},
    active: true,
    canEdit: null,
    canPickDash: null,
    currentRange: null,
    dashboardNames: [],
    data: {},
    display: null,
    displays: [],
    displayNames: [],
    expand: false,
    loadingRange: null,
    multiDash: null,
    range: {anchor: 'relative', period: 3600},
    refresh: 60,
    saving: null,
})

const primary = ref(null)
const confirm = ref(null)
const {Dashboard} = getModels()

onMounted(() => {
    let features = State.config.features || {}
    let board = props.board
    if (props.board.type == 'page') {
        page.canEdit = can('owner') || props.design
    } else {
        page.canEdit = can('admin') && (features.dash.edit || props.design)
    }
    page.dashboardNames = State.cache.dashboards.map((d) => d.name).sort()
    page.displays = State.app.fullDisplay.displays || []
    page.displayNames = page.displays.map((d) => d.name)
    page.display = State.app.display
    page.refresh = getValue(board.refresh || State.config?.timeouts?.dashboard || 60)

    page.multiDash = features.dash?.multiple
    Object.assign(page.range, board.range)
    page.currentRange = clone(page.range)

    page.canPickDash =
        props.board.type == 'dashboard' &&
        page.canEdit &&
        page.multiDash &&
        page.dashboardNames.length > 1 &&
        !(State.app.needs.save && page.mobile)

    watch(
        () => page.range,
        () => rangeUpdated(),
        {deep: true}
    )
})

onUnmounted(() => {
    page.active = false
})

function update() {
    let widget = props.widget
    Object.assign(page, getWidgetProps(widget, {primary}))
    page.dashboardNames = State.cache.dashboards.map((d) => d.name).sort()
    Object.assign(page.range, props.board.range)
}

function percent() {
    if (props.design) {
        return 100
    }
    return Math.max(0, 100 - (props.board._remaining / page.refresh) * 100)
}

async function rangeUpdated() {
    page.loadingRange = true
    await waitRender()
    if (State.auth.ready) {
        if (!page.currentRange || page.range.period != page.currentRange.period) {
            page.currentRange = clone(page.range)
            props.board.range = page.range
            State.app.setNeedSave(props.board)
        }
        refreshData()
    }
    page.loadingRange = false
}

function refreshData() {
    props.board._remaining = 0
    State.app.setNeed('refreshData')
}

function reloadBoard() {
    if (props.board.type == 'page') {
        State.app.noNeed('deploy')
    } else {
        State.app.setNeed('save')
    }
    State.app.noNeed('save')
    State.app.setNeed('reload')
}

function editBoard() {
    State.app.setNeed('editBoard')
}

function editWidget() {
    State.app.setNeed('editWidget')
}

function expandWidgets() {
    State.app.setNeedSave(props.board)
    State.app.setNeed('expandWidgets')
}

function showBoard() {
    let path = getRoute().query.path
    if (path) {
        go(path)
    } else if (props.board.type == 'dashboard') {
        go({name: 'show-dash', params: {id: props.board.id}})
    } else {
        go('/')
    }
}

async function changeDisplay(name) {
    State.app.setDisplay(page.displays.find((d) => d.name == name))
}

async function changeDashboard(name) {
    let dashboard = State.cache.dashboards.find((d) => d.name == name)
    await Dashboard.set(dashboard)
    State.app.setNeed('reload')
}

async function invokeDesign() {
    let name = props.board.type == 'page' ? 'page-editor' : 'dash-editor'
    let path = window.location.pathname + window.location.hash.replace(/^#\//, '')
    State.ref.router.push({name, query: {design: true, path}}).catch(() => {})
}

async function save() {
    await Progress.run(
        async () => {
            let board = clone(props.board)
            if (board.type == 'page') {
                let {key} = getView(props.view.path)
                if (key >= 0) {
                    if (board.componentName) {
                        board.component = getRoutes().getComponent(board.componentName)
                        delete board.componentName
                    }
                    let view = clone(props.view)
                    view.board = Dashboard.clean(board)
                    await updateView(view)
                    await persistDisplay()
                    Feedback.info('Please wait a minute for the app to be deployed')
                }
            } else {
                board.range = page.range
                await Dashboard.set(board)
                await Dashboard.update(board, {exists: true})
            }
            State.app.noNeed('save')
        },
        {page, field: 'saving', feedback: props.board.type == 'page' ? 'Deployed' : 'Saved'}
    )
}

function needSave() {
    let board = props.board
    if (board.type == 'dashboard') {
        return State.app.needs.save
    } else if (board.type == 'page') {
        return State.app.needs.deploy
    }
    return false
}
</script>

<template>
    <v-toolbar ref="primary" density="compact" class="toolbar-widget">
        <div
            v-if="!props.design && board.name"
            class="title"
            :class="State.app.dark ? 'dark' : 'light'">
            {{ board.name }}
        </div>
        <div v-else-if="props.design && props.view" class="title" :class="State.app.dark ? 'dark' : 'light'">
            {{ view.title || view.path }}
        </div>
        <div class="toolbar-icons">
            <vu-pick
                v-if="page.displays.length > 1"
                align="right"
                class="pick-dashboard mr-4"
                :label="page.display.name"
                :items="page.displayNames"
                @click="changeDisplay" />

            <vu-pick
                v-if="page.canPickDash"
                align="right"
                class="pick-dashboard mr-4"
                :label="board.name"
                :items="page.dashboardNames"
                @click="changeDashboard" />

            <vu-date
                v-model="page.range"
                class="date-picker mr-2"
                mode="date"
                :loading="page.loadingRange"
                size="small" />

            <v-btn
                v-if="board?.widgets?.length"
                variant="text"
                class="icon reload"
                v-tip.bottom="'Reload Data'"
                @mousedown.stop="refreshData">
                <v-progress-circular
                    :size="26"
                    :width="3"
                    :model-value="percent()"
                    color="primary-lighten-1">
                    <v-icon v-if="props.design" icon="$reload" />
                    <span v-else>{{ board._remaining }}</span>
                </v-progress-circular>
            </v-btn>

            <v-icon
                v-if="props.design && page.canEdit && board.layout == 'grid'"
                icon="$stretch"
                class="icon"
                size="large"
                v-tip.bottom="'Expand Widgets to Fill Rows'"
                color="primary-lighten-1"
                @mousedown.stop="expandWidgets" />

            <v-icon
                v-if="props.design && page.canEdit"
                icon="$dashboard"
                class="icon"
                size="large"
                v-tip.bottom="'Modify'"
                :color="true ? 'primary-lighten-1' : ''"
                @mousedown.stop="editBoard(board)" />

            <v-icon
                v-if="props.design && page.canEdit"
                icon="$plusBoxOutline"
                class="icon"
                size="large"
                v-tip.bottom="'Add Widget'"
                :color="true ? 'primary-lighten-1' : 'secondary'"
                @mousedown.stop="editWidget({})" />

            <v-icon
                v-if="page.canEdit && !props.design"
                icon="$tools"
                class="icon"
                size="large"
                v-tip.bottom="design ? 'Show' : 'Designer'"
                :color="board.design ? 'accent' : 'primary'"
                @mousedown.stop="invokeDesign()" />

            <v-icon
                v-if="props.design"
                icon="$launch"
                class="icon"
                size="large"
                v-tip.bottom="'Show ' + (board?.type == 'dashboard' ? 'Dashboard' : 'Page')"
                :color="true ? 'primary-lighten-1' : 'secondary'"
                @mousedown.stop="showBoard()" />

            <v-btn
                v-if="page.canEdit && needSave() && (props.design || props.board.type == 'dashboard')"
                size="small"
                class="dash-save"
                :loading="page.saving"
                @mousedown.stop.stop="save">
                {{ board.type == 'page' ? 'Deploy' : 'Save' }}
            </v-btn>

            <v-btn
                v-if="needSave() && props.design"
                class="dash-btn"
                variant="outlined"
                size="small"
                @mousedown.stop.stop="reloadBoard">
                Reset
            </v-btn>
        </div>
    </v-toolbar>
    <vu-confirm ref="confirm" />
</template>

<style lang="scss" scoped>
.v-toolbar {
    width: 100%;
    height: 68px !important;
    display: inline-block;
    padding: 14px 8px 8px 20px;
    z-index: 100;
    max-width: unset !important;
    border-bottom: 1px solid rgb(var(--v-theme-border));
    background: rgb(var(--v-theme-surface));

    .title {
        font-size: 28px;
        margin-right: 16px;
        vertical-align: middle;
        padding-bottom: 6px;
        color: rgb(var(--v-theme-primary));
        font-weight: normal;
    }
    .title.dark {
        color: rgb(var(--v-theme-accent));
    }
    .v-btn {
        font-size: 0.8rem;
    }
    .dashname {
        flex-grow: 0;
    }
    .pick-dashboard {
        cursor: pointer;
        .v-btn {
            font-size: 1rem;
        }
    }
    .vu-pick {
        margin-right: 8px !important;
        .v-btn {
            font-size: 0.75rem;
        }
    }
    .toolbar-icons {
        display: inline-flex;
        vertical-align: middle;
        align-items: flex-end;
        height: 36px;
        padding-bottom: 4px;
        .icon {
            margin-right: 8px;
            height: 26px;
            font-size: 22px;
            padding-bottom: 8px;
            cursor: pointer;
            @media (max-width: 640px) {
                margin-right: 8px;
            }
        }
        .reload {
            background: none !important;
            box-shadow: none !important;
            border: none !important;
            height: 20px !important;
            width: 20px !important;
            min-width: 0 !important;
            margin: 0 4px 10px 0 !important;
        }
        .reload:hover {
            color: rgba(0, 0, 0, 0);
        }
        .extras {
            border: 1px solid rgb(var(--v-theme-border));
            color: rgb(var(--v-theme-primary));
        }
    }
    .date-range {
        .v-btn {
            min-width: unset !important;
            font-size: 0.75rem;
        }
    }
    .custom-input {
        width: 95px !important;
    }
    .dash-btn {
        margin: 0 10px 4px 4px !important;
        border: solid 1px rgb(var(--v-theme-border));
    }
    .dash-save {
        margin: 0 10px 4px 4px !important;
        background: rgb(var(--v-theme-primary));
        color: white;
    }
}
</style>

<style lang="scss">
.toolbar-widget {
    .v-toolbar__content {
        overflow: visible !important;
    }
}
</style>
